import { type FormEvent, useEffect, useState } from "react";
import { ApiError, api, type PaseCompaneroOut, type PlantillaNotif, type UserMini } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Modal from "@/ui/Modal";
import Toggle from "@/ui/Toggle";
import { useToast } from "@/ui/Toast";

/**
 * "Pasar a un companero": accion ad-hoc (NO el flujo oficial) para avisarle a
 * otro compa que un caso le toca. Le llega alerta in-app + web push + correo
 * (desde el SMTP corporativo de quien lo pasa). Opcionalmente reasigna el caso.
 */

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  casoId: number;
  casoFolio: string;
  candidatos: UserMini[];
  onDone: () => void;
};

export default function HandoffModal({
  open,
  onOpenChange,
  casoId,
  casoFolio,
  candidatos,
  onDone,
}: Props) {
  const toast = useToast();
  const me = useAuth((s) => s.user);
  const [destinatarioId, setDestinatarioId] = useState<number | "">("");
  const [mensaje, setMensaje] = useState("");
  const [reasignar, setReasignar] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [plantillas, setPlantillas] = useState<PlantillaNotif[]>([]);

  // No tiene sentido pasarte el caso a ti mismo.
  const opciones = candidatos.filter((u) => u.id !== me?.id);

  useEffect(() => {
    if (!open) return;
    setDestinatarioId("");
    setReasignar(false);
    api
      .get<PlantillaNotif[]>("/api/plantillas-notificacion")
      .then((ps) => {
        setPlantillas(ps);
        setMensaje(ps[0]?.texto ?? "");
      })
      .catch(() => {
        setPlantillas([]);
        setMensaje("");
      });
  }, [open]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (destinatarioId === "") return;
    setSubmitting(true);
    try {
      const r = await api.post<PaseCompaneroOut>(`/api/casos/${casoId}/pase`, {
        destinatario_id: destinatarioId,
        mensaje: mensaje.trim(),
        reasignar,
      });
      const detalle = [
        "Alerta enviada",
        r.correo_enviado ? "correo enviado" : "sin correo (revisa tu correo)",
        r.reasignado ? "caso reasignado" : null,
      ]
        .filter(Boolean)
        .join(" · ");
      toast.success("Caso pasado al compañero", detalle);
      onDone();
      onOpenChange(false);
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude pasar el caso", msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Pasar el caso a un compañero"
      description={`Caso ${casoFolio} — le llega alerta + correo. No es el flujo oficial, solo un aviso.`}
      size="md"
      footer={
        <>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="btn-ghost text-sm"
            disabled={submitting}
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="handoff-form"
            className="btn-primary text-sm"
            disabled={destinatarioId === "" || submitting}
          >
            {submitting ? "Enviando..." : "Notificar"}
          </button>
        </>
      }
    >
      <form id="handoff-form" onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label className="block text-xs uppercase tracking-wide text-zinc-500 mb-1">
            Pasar a
          </label>
          <select
            className="input w-full"
            value={destinatarioId}
            onChange={(e) =>
              setDestinatarioId(e.target.value ? parseInt(e.target.value) : "")
            }
          >
            <option value="">— elige un compañero —</option>
            {opciones.map((u) => (
              <option key={u.id} value={u.id}>
                {u.nombre}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wide text-zinc-500 mb-1">
            Mensaje rápido
          </label>
          <select
            className="input w-full mb-2"
            value={plantillas.some((p) => p.texto === mensaje) ? mensaje : ""}
            onChange={(e) => {
              if (e.target.value) setMensaje(e.target.value);
            }}
          >
            <option value="">— plantilla (opcional) —</option>
            {plantillas.map((p) => (
              <option key={p.id} value={p.texto}>
                {p.texto}
              </option>
            ))}
          </select>
          <textarea
            className="input w-full"
            rows={3}
            value={mensaje}
            onChange={(e) => setMensaje(e.target.value)}
            placeholder="Escribe o ajusta el mensaje del aviso..."
          />
        </div>

        <Toggle
          checked={reasignar}
          onChange={setReasignar}
          label="También asignar el caso a esta persona"
          className="text-sm"
        />
      </form>
    </Modal>
  );
}
